import {
  faArrowTurnDownLeft,
  faCalendarAlt,
  faCube,
} from "@fortawesome/pro-regular-svg-icons";
import Quill from "quill";
import "quill-mention/autoregister";
import { useCallback, useEffect, useRef } from "react";

import { createRoot } from "react-dom/client";
import { twJoin } from "tailwind-merge";

import { NodeBE } from "src/api/flowTypes";
import { Divider } from "src/base-components/Divider";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { EventConfigEnriched } from "src/clients/features-control/api";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { getNodeIconFromNode } from "src/constants/NodeTypeIcons";
import { MentionBlot } from "src/copilot/MentionBlot";
import { getQuillContent, MentionedResource } from "src/copilot/quillUtils";
import { useInitQuill } from "src/copilot/useInitQuill";
import { ResourceType } from "src/copilot/utils";
import { Button } from "src/design-system/Button";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { entityIcons } from "src/entities/entityView/utils";
import { EntitySchemaResource } from "src/entities/queries";

type Props = {
  value: string;
  disabled: boolean;
  minified: boolean;
  errored: boolean;
  onReset: () => void;
  onChange: (value: string) => void;
  onSubmit: (
    content: string,
    mentionedResources: (EntitySchemaResource | EventConfigEnriched | NodeBE)[],
  ) => void;
  resourceType: ResourceType;
  resource: MentionedResource[];
  mentionTextPlaceholder: string;
  topContent?: React.ReactNode;
  submitLabel?: string;
};

type MentionItemType = MentionedResource & {
  id: string;
  value: string;
};

const MentionItemComponent = ({
  resource,
  resourceType,
}: {
  resource: MentionItemType;
  resourceType: ResourceType;
}) => {
  const renderIcon = () => {
    if (
      resourceType ===
      FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
    ) {
      const entity = resource as EntitySchemaResource;
      const icon =
        "_display_name_singular" in resource
          ? (entityIcons[entity._display_symbol] ?? faCube)
          : faCalendarAlt;
      return <Icon icon={icon} size="xs" />;
    } else {
      const node = resource as BeMappedNode;
      return getNodeIconFromNode(node, "sm");
    }
  };

  const renderLabel = () => {
    if (
      resourceType ===
      FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
    )
      return (
        <div className="flex items-center gap-2">
          <p>{resource.value}</p>
        </div>
      );
    const node = resource as BeMappedNode;
    return <p>{node.data.label}</p>;
  };

  const renderTypeLabel = () => {
    if (
      resourceType ===
      FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
    ) {
      const isEntity = "_display_name_singular" in resource;
      return (
        <Pill size="sm" variant="gray">
          <Pill.Text>{isEntity ? "Entity" : "Event"}</Pill.Text>
        </Pill>
      );
    }
    return null;
  };

  return (
    <div className="flex w-[296px] cursor-pointer items-center justify-between px-3 py-4 hover:bg-gray-100">
      <div className="flex items-center gap-2">
        <div className="h-5 w-5 shrink-0">{renderIcon()}</div>
        {renderLabel()}
      </div>
      {renderTypeLabel()}
    </div>
  );
};

export const renderDropdownItem = (
  resource: MentionItemType,
  resourceType: ResourceType,
) => {
  const container = document.createElement("div");
  const root = createRoot(container);
  root.render(
    <MentionItemComponent resource={resource} resourceType={resourceType} />,
  );
  return container;
};

Quill.register("formats/mention", MentionBlot);

export const PromptBox: React.FC<Props> = ({
  disabled,
  value,
  minified,
  errored,
  onReset,
  onChange,
  onSubmit,
  resourceType,
  resource,
  mentionTextPlaceholder,
  topContent,
  submitLabel = "Start Chat",
}) => {
  const editorRef = useRef<HTMLDivElement>(null);

  const handleSubmit = useCallback(
    (quill: Nullable<Quill>) => {
      if (disabled) return;
      const { content, mentionedResources } = getQuillContent(
        quill,
        resource,
        resourceType,
      );
      onSubmit(content ?? "", mentionedResources);
    },
    [disabled, resource, resourceType, onSubmit],
  );

  const submitRef = useRef(handleSubmit);
  const quill = useInitQuill({
    editorRef,
    resource,
    resourceType,
    minified,
    submitRef,
    value,
    onChange,
  });

  useEffect(() => {
    setTimeout(() => {
      if (quill.current) {
        /* When the prompt box is mounted with some initial value, we need to focus the editor and set the selection to the end of the content.
          We should use setSelection only as it handles focus out of the box.
        */
        quill.current.setSelection(quill.current.getLength(), 0);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    submitRef.current = handleSubmit;
  }, [handleSubmit]);

  if (errored) {
    return (
      <div className="broder-gray-200 flex items-center justify-center rounded-lg border bg-gray-50 p-3.5">
        <Button
          iconRight={faArrowTurnDownLeft}
          size="sm"
          variant="primary"
          onClick={onReset}
        >
          Start a new chat
        </Button>
      </div>
    );
  }

  return (
    <div className="broder-gray-200 flex flex-col rounded-lg border bg-gray-0 shadow-sm">
      {topContent && (
        <div className="px-3">
          <div className="py-2">{topContent}</div>
          <Divider />
        </div>
      )}
      <div className="relative">
        <div
          ref={editorRef}
          className={twJoin(
            "resize-none",
            "border border-transparent",
            "pt-2 pb-2.5 pl-3",
            "font-inter-normal-13px text-gray-800",
            "focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
            minified ? "rounded-lg" : "rounded-t-lg",
            minified ? "pr-24" : "pr-3",
          )}
          data-loc="copilot-prompt"
        />
        {minified && (
          <div className="absolute top-2 right-2">
            <Button
              isDisabled={disabled}
              htmlType="submit"
              iconRight={faArrowTurnDownLeft}
              size="sm"
              variant="primary"
              onClick={() => handleSubmit(quill.current)}
            >
              Continue
            </Button>
          </div>
        )}
      </div>
      {!minified && (
        <div className="mx-3 flex items-center justify-between border-t border-t-gray-100 py-2">
          <p className="font-inter-normal-12px text-gray-500">
            {mentionTextPlaceholder}
          </p>
          <Button
            dataLoc="copilot-start-chat"
            isDisabled={disabled}
            htmlType="submit"
            iconRight={faArrowTurnDownLeft}
            size="sm"
            variant="primary"
            onClick={() => handleSubmit(quill.current)}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
